import { Form, useField, useFormikContext } from 'formik';
import FormElement from 'components/global/FormElement';
import CustomSelect from 'components/global/CustomSelect';
import {
  ExplanatoryParagraphProps,
  FormElementPropsType,
  HorizontalInputPairProps,
  HorizontalInputTrioProps,
  StaticInputProps,
  HorizontalSingleInputProps,
} from '../../../types/stepFlow.type';
import RadioGroup from '../../global/RadioGroup';
import styles from './stepForm.module.scss';
import React, { Key, useEffect } from 'react';
import Tooltip from '../../global/Tooltip';
import { ReactComponent as InfoIcon } from 'assets/vectors/info.svg';
import { FormikCheckbox, FormikCheckboxProps } from '../../global/Checkbox';
import { useAppSelector } from 'redux/hooks';

interface TrialStepFormProps {
  children?: React.ReactNode;
  rightHalfElement?: React.ReactNode;
  formElements: FormElementPropsType[];
  valuesCallback?: (values: any, touched: any) => void;
}

interface StaticTextFieldProps {
  element: StaticInputProps;
}

const StaticTextField: React.FC<StaticTextFieldProps> = ({ element }) => {
  const [field] = useField(element.name);

  return (
    <div className={styles['step-form-static-text']}>
      <div className={styles['step-form-static-text__label']}>
        {'label' in element && element?.label ? element.label : 'No Label'}
        {'tooltip' in element && element?.tooltip ? (
          <Tooltip label={element.tooltip}>
            <InfoIcon />
          </Tooltip>
        ) : null}
      </div>
      <div className={styles['step-form-static-text__value']}>
        {('displayValue' in element && element?.displayValue) || element.value}
        {/* Hidden input field so Formik can see these */}
        {'name' in element && element?.name ? (
          <input type="hidden" {...field} />
        ) : null}
      </div>
    </div>
  );
};

const SourceValueFromState = (sourcedValueStateProperty: string) => {
  const state = useAppSelector((state) => {
    return sourcedValueStateProperty
      .split('.')
      .reduce(
        (acc: any, key: string) => (acc && acc[key] ? acc[key] : undefined),
        state,
      );
  });

  return state;
};

const renderElement = (
  element: FormElementPropsType,
  index: Key | null | undefined,
  rest: Record<string, any>,
  formValues: Record<string, any>,
) => {
  // Override form values dynamically
  if (
    'name' in element &&
    'sourceValueFromState' in element &&
    'sourcedValueStateProperty' in element &&
    element.sourcedValueStateProperty &&
    !formValues[element.name]
  ) {
    formValues[element.name] = SourceValueFromState(
      element?.sourcedValueStateProperty,
    );
  }
  // Check if this element should be conditionally rendered
  if ('condition' in element && element.condition) {
    const {
      field,
      value,
      values: condValues,
      operator = 'equals',
    } = element.condition;
    const fieldValue = formValues[field];

    // Skip rendering if condition is not met
    if (operator === 'equals' && fieldValue !== value) return null;
    if (operator === 'not-equals' && fieldValue === value) return null;
    if (operator === 'in' && condValues && !condValues.includes(fieldValue))
      return null;
    if (operator === 'not-in' && condValues && condValues.includes(fieldValue))
      return null;
  }

  index = `${index}-${element.element}-${
    ('name' in element && element?.name) || 'no-field-name'
  }`;

  // For horizontalInputPair, we need to check conditions on each child element
  if (element.element === 'horizontalInputPair') {
    const typedElement = element as HorizontalInputPairProps;
    const filteredElements = typedElement.elements.filter(
      (childElement: FormElementPropsType) => {
        if ('condition' in childElement && childElement.condition) {
          const {
            field,
            value,
            values: condValues,
            operator = 'equals',
          } = childElement.condition;
          const fieldValue = formValues[field];

          if (operator === 'equals' && fieldValue !== value) return false;
          if (operator === 'not-equals' && fieldValue === value) return false;
          if (
            operator === 'in' &&
            condValues &&
            !condValues.includes(fieldValue)
          )
            return false;
          if (
            operator === 'not-in' &&
            condValues &&
            condValues.includes(fieldValue)
          )
            return false;
        }
        return true;
      },
    );

    // Only render the horizontalInputPair if it has elements to show
    if (filteredElements.length === 0) return null;

    return (
      <div
        className={styles['step-form-horizontal-pair-container']}
        key={index}
      >
        {filteredElements.map(
          (childElement: FormElementPropsType, childIndex: number) =>
            renderElement(
              childElement,
              `${index}-${childIndex}`,
              rest,
              formValues,
            ),
        )}
      </div>
    );
  }

  // For horizontalInputTrio, same logic as pair but with trio styling
  if (element.element === 'horizontalInputTrio') {
    const typedElement = element as HorizontalInputTrioProps;
    const filteredElements = typedElement.elements.filter(
      (childElement: FormElementPropsType) => {
        if ('condition' in childElement && childElement.condition) {
          const {
            field,
            value,
            values: condValues,
            operator = 'equals',
          } = childElement.condition;
          const fieldValue = formValues[field];

          if (operator === 'equals' && fieldValue !== value) return false;
          if (operator === 'not-equals' && fieldValue === value) return false;
          if (
            operator === 'in' &&
            condValues &&
            !condValues.includes(fieldValue)
          )
            return false;
          if (
            operator === 'not-in' &&
            condValues &&
            condValues.includes(fieldValue)
          )
            return false;
        }
        return true;
      },
    );

    // Only render the horizontalInputTrio if it has elements to show
    if (filteredElements.length === 0) return null;

    return (
      <div
        className={styles['step-form-horizontal-trio-container']}
        key={index}
      >
        {filteredElements.map(
          (childElement: FormElementPropsType, childIndex: number) =>
            renderElement(
              childElement,
              `${index}-${childIndex}`,
              rest,
              formValues,
            ),
        )}
      </div>
    );
  }

  if (element.element === 'horizontalSingleInput') {
    const typedElement = element as HorizontalSingleInputProps;
    const childElement = typedElement.inputElement;

    // Check condition on the child element
    if ('condition' in childElement && childElement.condition) {
      const {
        field,
        value,
        values: condValues,
        operator = 'equals',
      } = childElement.condition;
      const fieldValue = formValues[field];

      if (operator === 'equals' && fieldValue !== value) return null;
      if (operator === 'not-equals' && fieldValue === value) return null;
      if (operator === 'in' && condValues && !condValues.includes(fieldValue))
        return null;
      if (
        operator === 'not-in' &&
        condValues &&
        condValues.includes(fieldValue)
      )
        return null;
    }

    return (
      <div
        className={styles['step-form-horizontal-single-container']}
        key={index}
      >
        {renderElement(childElement, `${index}-child`, rest, formValues)}
      </div>
    );
  }

  switch (element.element) {
    case 'select':
      return (
        <CustomSelect
          name={element.name}
          label={('label' in element && element.label) || element.name}
          direction={('direction' in element && element?.direction) || 'row'}
          options={('options' in element && element.options) || []}
          key={index}
          allowOther={('allowOther' in element && element.allowOther) || false}
          otherInputLabel={
            ('otherInputLabel' in element && element.otherInputLabel) ||
            element.name
          }
          {...element}
        />
      );
    case 'radioGroup':
      return (
        <RadioGroup
          name={element.name ? element.name : 'radioGroup'}
          key={index}
          options={element?.options ? element.options : []}
          align={
            'align' in element && element?.align ? element.align : undefined
          }
          radioGroupHeader={
            ('radioGroupHeader' in element && element?.radioGroupHeader) ||
            undefined
          }
          radioGroupFooter={
            ('radioGroupFooter' in element && element?.radioGroupFooter) ||
            undefined
          }
          defaultValue={
            Array.isArray(element.value)
              ? element.value[0]
              : typeof element.value === 'number'
              ? element.value.toString()
              : element.value
          }
          {...rest}
          onChange={element.onChange ?? (() => {})}
        />
      );
    case 'horizontalDivider':
      return (
        <div className={styles['step-form-horizontal-divider']} key={index} />
      );
    case 'sectionHeading':
      let style = styles['step-form-section-heading'];
      if ('align' in element && element.align === 'singleInput') {
        style = styles['step-form-section-heading-single-input'];
      }
      return (
        <div key={index} className={style}>
          <div>
            {'label' in element && element?.label ? element.label : 'No Label'}
            {'tooltip' in element && element?.tooltip ? (
              <Tooltip label={element.tooltip}>
                <InfoIcon />
              </Tooltip>
            ) : null}
          </div>
        </div>
      );
    case 'explanatoryParagraphs':
      return (
        <div className={styles['step-form-explanatory-paragraphs']} key={index}>
          {(element as ExplanatoryParagraphProps).paragraphTexts.map(
            (text, i) => (
              <p key={i}>{text}</p>
            ),
          )}
        </div>
      );
    case 'checkbox':
      return (
        <FormikCheckbox key={index} {...(element as FormikCheckboxProps)} />
      );
    case 'staticText': {
      const staticElement = element as StaticInputProps;
      const rawValue =
        // support dynamically pulling static text values from other fields in the form
        staticElement.displayValue?.startsWith('{{') &&
        staticElement.displayValue.endsWith('}}')
          ? formValues[
              staticElement.displayValue
                .slice(2, -2)
                .trim() as keyof typeof formValues
            ]
          : staticElement.displayValue;

      return (
        <StaticTextField
          element={{
            ...staticElement,
            element: 'staticText',
            // allow for a transform function to be applied to the raw value, e.g. split "bu/ac" to "bu"
            displayValue: staticElement.transform
              ? staticElement.transform(rawValue || '')
              : rawValue,
          }}
          key={index}
        />
      );
    }
    case 'summaryField':
      return (
        <div className={styles['step-form-summary-field']} key={index}>
          <div className={styles['step-form-summary-field__label']}>
            {element.label}:
          </div>
          <div className={styles['step-form-summary-field__value']}>
            {element.value}
          </div>
        </div>
      );
    default:
      return <FormElement {...element} direction="row" key={index} />;
  }
};

const StepForm = ({
  formElements,
  children,
  rightHalfElement,
  valuesCallback,
  ...rest
}: TrialStepFormProps) => {
  const { values, touched } = useFormikContext<Record<string, any>>();
  useEffect(() => {
    if (valuesCallback) {
      valuesCallback(values, touched);
    }
  }, [values, valuesCallback, touched]);
  return (
    <>
      <Form key={'step-form-actual-form'}>
        {formElements?.length
          ? formElements.map((element, index) =>
              renderElement(element, index, rest, values),
            )
          : null}
        {children}
      </Form>
      {rightHalfElement && rightHalfElement}
    </>
  );
};

export default StepForm;
